import { Link } from 'react-router-dom';
import SEO from '../../components/base/SEO';

export default function ServicesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: [
      {
        '@type': 'Service',
        position: 1,
        name: 'Double Wall Chimney Systems',
        description: 'High-performance double wall chimney systems designed for maximum insulation and safety.',
        url: `${siteUrl}/services/double-wall`,
        provider: {
          '@type': 'Organization',
          name: 'Proform Chimney Systems'
        }
      },
      {
        '@type': 'Service',
        position: 2,
        name: 'Single Wall Chimney Systems',
        description: 'Reliable single wall chimney systems for efficient gas discharge.',
        url: `${siteUrl}/services/single-wall`,
        provider: {
          '@type': 'Organization',
          name: 'Proform Chimney Systems'
        }
      },
      {
        '@type': 'Service',
        position: 3,
        name: 'Hermetic Chimney Systems',
        description: 'Advanced hermetic chimney systems for condensing and non-condensing heating devices.',
        url: `${siteUrl}/services/hermetic`,
        provider: {
          '@type': 'Organization',
          name: 'Proform Chimney Systems'
        }
      }
    ]
  };

  const services = [
    {
      title: 'Double Wall Chimney Systems',
      description: 'High-performance double wall chimney systems designed for maximum insulation and safety. Perfect for residential and commercial applications requiring superior heat retention and protection.',
      image: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/e709212b1013501d70c8a486a02a15e6.jpeg',
      features: [
        'Superior insulation performance',
        'Enhanced safety features',
        'Reduced heat loss',
        'Long-lasting durability'
      ],
      link: '/services/double-wall',
      icon: 'ri-stack-line'
    },
    {
      title: 'Single Wall Chimney Systems',
      description: 'Reliable single wall chimney systems for efficient gas discharge. Ideal for various heating applications with solid, liquid, or gaseous fuel sources.',
      image: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/98c227dafab0f6df452e03b8827d7faa.jpeg',
      features: [
        'Cost-effective solution',
        'Easy installation',
        'Flexible mounting options',
        'Industry-standard materials'
      ],
      link: '/services/single-wall',
      icon: 'ri-layout-column-line'
    },
    {
      title: 'Hermetic Chimney Systems',
      description: 'Advanced hermetic chimney systems for condensing and non-condensing heating devices. Ensures safe air intake and exhaust discharge for optimal performance.',
      image: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/7c7c16c4859bfb1f98160792a2b2369e.jpeg',
      features: [
        'Dual-function design',
        'Gas compatibility',
        'Efficient air circulation',
        'Secure operation'
      ],
      link: '/services/hermetic',
      icon: 'ri-shield-check-line'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'ri-medal-line',
      title: 'Premium Quality',
      description: '316L and 304 stainless steel materials with ISO certification'
    },
    {
      icon: 'ri-tools-line',
      title: 'Expert Craftsmanship',
      description: 'Precision laser cutting and TIG welding technology'
    },
    {
      icon: 'ri-team-line',
      title: 'Professional Team',
      description: '15+ years of experience in chimney systems'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Dedicated customer service and maintenance'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Safety First',
      description: 'Compliance with all international safety standards'
    },
    {
      icon: 'ri-global-line',
      title: 'Global Reach',
      description: 'Serving customers across Turkey and international markets'
    }
  ];

  return (
    <>
      <SEO
        title="Chimney Services Turkey - Double Wall, Single Wall & Hermetic Systems | Proform"
        description="Comprehensive chimney solutions in Turkey including double wall, single wall, and hermetic chimney systems. Expert installation and maintenance for residential and commercial applications."
        keywords="chimney services Turkey, double wall chimney, single wall chimney, hermetic chimney, chimney installation, chimney maintenance"
        canonical={`${siteUrl}/services`}
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section with Background Image */}
        <section className="relative h-[600px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/54f21c0f78b93822820891351976ae5d.jpg"
              alt="Chimney Services"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a]/95 via-[#1e40af]/90 to-[#1e3a8a]/95"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-center text-white">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <Link to="/" className="hover:text-orange-400 transition-colors">Home</Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-orange-400 font-semibold">Services</li>
              </ol>
            </nav>
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">Our Services</h1>
            <p className="text-2xl md:text-3xl max-w-3xl leading-relaxed">
              Comprehensive chimney solutions engineered for excellence, safety, and performance
            </p>
            <div className="flex items-center gap-8 mt-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <span className="text-lg font-semibold">ISO Certified</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <span className="text-lg font-semibold">15+ Years Experience</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <span className="text-lg font-semibold">500+ Projects</span>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid - Larger Cards */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Our Chimney Systems</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose from our range of professional chimney solutions designed to meet your specific needs
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    <div className="absolute top-6 right-6 w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <i className={`${service.icon} text-3xl text-white`}></i>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{service.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed text-lg">{service.description}</p>
                    
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-gray-700">
                          <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-orange-500 text-sm"></i>
                          </div>
                          <span className="font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link
                      to={service.link}
                      className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 w-full justify-center whitespace-nowrap shadow-lg hover:shadow-xl group"
                    >
                      <span>Explore Details</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Proform - Redesigned */}
        <section className="py-24 bg-gradient-to-br from-[#1e3a8a] via-[#1e40af] to-[#1e3a8a] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">Why Choose Proform?</h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto">
                We deliver excellence in every chimney system we manufacture and install
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {whyChooseUs.map((item, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 hover:bg-white/20 transition-all duration-300 border border-white/20 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-white/80 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">Ready to Get Started?</h2>
            <p className="text-2xl text-white/90 mb-10 leading-relaxed">
              Contact us today to discuss your chimney system requirements and get a free consultation
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-white text-orange-500 px-10 py-5 rounded-full text-xl font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-2xl"
              >
                <i className="ri-mail-line text-2xl"></i>
                <span>Contact Us</span>
              </Link>
              <a
                href="https://wa.me/905326304227"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-10 py-5 rounded-full text-xl font-bold hover:bg-[#20BA5A] transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-2xl"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}